import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useEffect, useState } from 'react';
import { ConfirmModalMEP } from './ConfirmModalMEP.jsx';

export const FormUpdateMEP = ({
  data,
  onUpdate,
  onCancel,
  onClose,
  profile,
  systemChange,
}) => {
  const [dates, setDates] = useState({
    start: null,
    end: null,
  });
  const date = new Date(data.scheduledAt);
  const [limit, setLimit] = useState({
    start: new Date(),
    end: date.setMinutes(date.getUTCMinutes() + 15),
  });
  const [formError, setFormError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [update, setUpdate] = useState(null);

  const handleSubmit = () => {
    setOpenModal(false);
    onUpdate(update);
    onClose(null);
  };

  const cancelUpdate = () => {
    setOpenModal(false);
    setDates({ ...dates, start: null, end: null });
  };

  const handleCancel = () => {
    const content = `Déploiement annulé, vous ne serez pas déconnectés`;
    const cancelUpdate = {
      code: 'ACTIVE',
      scheduledAt: null,
      estimedEndAt: null,
      author: profile,
      endAt: null,
      startAt: null,
      message: content,
    };
    onCancel(cancelUpdate);
    onClose(null);
  };

  const handleConfirmModal = () => {
    if (dates.start === null) {
      setFormError('Veuillez renseigner une date de début valide');
      return;
    }
    setFormError(null);
    const start = new Date(dates?.start).toLocaleTimeString('fr', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const end = new Date(dates?.end).toLocaleTimeString('fr', {
      hour: '2-digit',
      minute: '2-digit',
    });
    let content = `RAPPEL: vous serez déconnectés à ${start} pour cause de déploiement. Veillez à sauvegarder votre travail.`;
    if (dates?.end != null) {
      content = content + ` Fin du déploiement prévu à ${end} !`;
    }
    setUpdate({
      code: 'WAITING_DEPLOY',
      scheduledAt: dates.start,
      estimedEndAt: dates?.end,
      updatedAt: data?.updatedAt,
      author: profile,
      endAt: null,
      startAt: null,
      message: content,
    });
    setDates({ ...dates, start: null, end: null });
    setOpenModal(true);
  };

  const launchDeploy = () => {
    const today = new Date();
    const launchUpdate = {
      code: 'DEPLOYING',
      author: profile,
      endAt: null,
      startAt: today,
    };
    systemChange(launchUpdate);
    onClose(null);
  };

  useEffect(() => {
    const minEnd = new Date(dates.start?.getTime());
    minEnd.setUTCMinutes(dates.start?.getUTCMinutes() + 15);
    setLimit({ ...limit, end: minEnd });
  }, [dates]);

  return (
    <Box
      sx={{
        height: '40rem',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start',
        gap: 1,
      }}
    >
      <Card
        sx={{
          width: '100%',
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FormControl
          error={Boolean(formError != null)}
          fullWidth
          sx={{ mt: 2 }}
        >
          <Typography
            color="primary"
            sx={{ fontSize: '1em', fontWeight: 'bold' }}
          >
            Modification du déploiement
          </Typography>

          <Typography color="primary" sx={{ fontSize: '1em' }}>
            Délais de déploiement
          </Typography>
          <Typography sx={{ fontSize: '0.9em', mt: 2 }}>
            Dates actuelles: {new Date(data.scheduledAt).toLocaleString('fr')} -{' '}
            {data?.estimedEndAt !== null
              ? new Date(data.scheduledAt).toLocaleString('fr')
              : 'Fin non renseignée'}
          </Typography>
          <Typography sx={{ fontSize: '0.8em', mt: 2 }}>
            Changer le début du déploiement changeras la date à laquelle les
            utilisateurs seront déconnectés.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 2,
              mt: 2,
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={fr}
            >
              <DateTimePicker
                label="Début"
                ampm={false}
                onChange={(newValue) => setDates({ ...dates, start: newValue })}
                minDateTime={limit.start}
              />
              <DateTimePicker
                label="Fin estimée"
                ampm={false}
                onChange={(newValue) => setDates({ ...dates, end: newValue })}
                minDateTime={limit.end}
              />
              <Button
                endIcon={<EditIcon />}
                variant="contained"
                color="primary"
                onClick={handleConfirmModal}
                disabled={dates.start === null}
              >
                Modifier
              </Button>
            </LocalizationProvider>
          </Box>
          {formError != null ? (
            <FormHelperText error>{formError}</FormHelperText>
          ) : null}
        </FormControl>

        {data?.author && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography
              color="primary"
              sx={{ fontSize: '0.8em', fontWeight: 'bold' }}
            >
              {data?.updatedAt
                ? 'Procédure de déploiement déclenchée le ' +
                  format(new Date(data?.updatedAt), "dd MMMM yyyy 'à' HH:mm ", {
                    locale: fr,
                  })
                : 'Aucune mise à jour '}
              par {data?.author?.firstName} {data?.author?.lastName}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" color="info" onClick={() => onClose(null)}>
            Annuler
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleCancel()}
          >
            Annuler Déploiement
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={launchDeploy}
            disabled={dates.start !== null}
          >
            Lancer Déploiement
          </Button>
        </Box>
      </Card>
      <ConfirmModalMEP
        isOpen={openModal}
        handleClose={() => cancelUpdate()}
        handleConfirm={() => handleSubmit()}
        formData={update}
      />
    </Box>
  );
};
